import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import LoadingCenter from '../util/LoadingCenter';
import SearchFilter from '../util/SearchFilter';

const lbl: React.CSSProperties = {
  display: 'block', marginBottom: 5, fontSize: 11, fontWeight: 800,
  letterSpacing: '.03em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)',
};

interface Holiday { name: string; date: string; recurring?: boolean }
interface HrDefaults {
  entitlementFormula?: string; entitlementWeeks?: number; entitlementDays?: number;
  leaveUnitBasis?: string; leaveYearStart?: string; accrualMode?: string; accrualRate?: number;
  rounding?: string; carryoverMaxDays?: number | null; publicHolidayPolicy?: string;
}

const MONTHS = Array.from({ length: 12 }, (_, m) => ({ value: m + 1, label: new Date(2000, m, 1).toLocaleString('en', { month: 'short' }) }));

const CountryDefaults: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [countries, setCountries] = useState<any[]>([]);
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [languages, setLanguages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sel, setSel] = useState<any | null>(null);
  const [hr, setHr] = useState<HrDefaults>({});
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [currencyId, setCurrencyId] = useState<number | ''>('');
  const [languageId, setLanguageId] = useState<number | ''>('');
  const [unit, setUnit] = useState('km');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, cur, lang] = await Promise.allSettled([
        axios.get('static-country/public/list'),
        axios.get('static-currency/public/list'),
        axios.get('static-language/public/list'),
      ]);
      if (c.status === 'fulfilled') setCountries((Array.isArray(c.value.data) ? c.value.data : []).slice().sort((a: any, b: any) => (a.name ?? '').localeCompare(b.name ?? '')));
      if (cur.status === 'fulfilled') setCurrencies((Array.isArray(cur.value.data) ? cur.value.data : []).slice().sort((a: any, b: any) => (a.iso_alpha3 ?? '').localeCompare(b.iso_alpha3 ?? '')));
      if (lang.status === 'fulfilled') setLanguages((Array.isArray(lang.value.data) ? lang.value.data : []).slice().sort((a: any, b: any) => (a.native_name ?? a.name ?? '').localeCompare(b.native_name ?? b.name ?? '')));
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const pick = (country: any) => {
    setSel(country);
    setHr(country.hr_defaults ?? {});
    setHolidays(Array.isArray(country.public_holidays) ? country.public_holidays : []);
    setCurrencyId(country.defaultCurrencyId ?? '');
    setLanguageId(country.defaultLanguageId ?? '');
    setUnit(country.default_unit ?? 'km');
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? countries.filter(c => c.name.toLowerCase().includes(q)) : countries;
  }, [countries, search]);

  const setHrField = (patch: Partial<HrDefaults>) => setHr(h => ({ ...h, ...patch }));
  const [mm, dd] = (hr.leaveYearStart || '01-01').split('-');

  const save = async () => {
    if (!sel) return;
    setSaving(true);
    try {
      await axios.put(`static-country/${sel.id}/defaults`, {
        defaultCurrencyId: currencyId === '' ? null : Number(currencyId),
        defaultLanguageId: languageId === '' ? null : Number(languageId),
        default_unit: unit,
        hr_defaults: hr,
        public_holidays: holidays,
      });
      enqueueSnackbar('Saved', { variant: 'success' });
      // обновить локально
      setCountries(cs => cs.map(c => c.id === sel.id ? { ...c, hr_defaults: hr, public_holidays: holidays, defaultCurrencyId: currencyId || null, defaultLanguageId: languageId || null, default_unit: unit } : c));
    } catch {
      enqueueSnackbar('Error', { variant: 'error' });
    } finally { setSaving(false); }
  };

  if (loading) return <LoadingCenter />;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 16, height: '75vh' }}>
      {/* Country list */}
      <div className="dt-card-wrap" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: 10, borderBottom: 'var(--border-input)' }}>
          <SearchFilter grow columns={[]} search={search} onSearch={setSearch} filters={[]} onFilters={() => {}} placeholder="Country…" />
        </div>
        <div style={{ flex: 1, overflow: 'auto' }}>
          {filtered.map(c => (
            <div key={c.id} onClick={() => pick(c)}
              style={{
                display: 'flex', alignItems: 'center', gap: 9, padding: '9px 12px', cursor: 'pointer',
                borderBottom: 'var(--border-input-one)', fontSize: 13,
                background: sel?.id === c.id ? 'color-mix(in srgb, var(--btn-user-btn) 60%, transparent)' : 'transparent',
                fontWeight: sel?.id === c.id ? 700 : 400,
              }}>
              <span>{c.flag_emoji}</span><span style={{ flex: 1 }}>{c.name}</span>
              {c.hr_defaults && <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--color-success)' }} />}
            </div>
          ))}
        </div>
      </div>

      {/* Editor */}
      <div className="dt-card-wrap" style={{ overflow: 'auto' }}>
        {!sel ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-color-calendar-week)' }}>← Select a country</div>
        ) : (
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 20 }}>{sel.flag_emoji}</span>
              <b style={{ fontSize: 15 }}>{sel.name}</b>
              <div style={{ flex: 1 }} />
              <button type="button" className="btn-primary" onClick={save} disabled={saving}>Save template</button>
            </div>

            {/* Locale */}
            <div className="section-label"><span className="tr-section-dot" style={{ background: '#2563eb' }} />Locale</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 12 }}>
              <div>
                <label style={lbl}>Currency</label>
                <select className="timesheet-edit-input w-full" value={currencyId} onChange={e => setCurrencyId(e.target.value === '' ? '' : Number(e.target.value))}>
                  <option value="">—</option>
                  {currencies.map(c => <option key={c.id} value={c.id}>{c.iso_alpha3} — {c.symbol}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Language</label>
                <select className="timesheet-edit-input w-full" value={languageId} onChange={e => setLanguageId(e.target.value === '' ? '' : Number(e.target.value))}>
                  <option value="">—</option>
                  {languages.map(l => <option key={l.id} value={l.id}>{l.native_name || l.name}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Units</label>
                <select className="timesheet-edit-input w-full" value={unit} onChange={e => setUnit(e.target.value)}>
                  <option value="km">km</option>
                  <option value="mi">mi</option>
                </select>
              </div>
            </div>

            {/* HR leave rules */}
            <div className="section-label"><span className="tr-section-dot" style={{ background: '#d97706' }} />Leave rules</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 12 }}>
              <div>
                <label style={lbl}>Counting unit</label>
                <select className="timesheet-edit-input w-full" value={hr.leaveUnitBasis || 'working_days'} onChange={e => setHrField({ leaveUnitBasis: e.target.value })}>
                  <option value="working_days">Working days</option>
                  <option value="calendar_days">Calendar days</option>
                  <option value="hours">Hours</option>
                </select>
              </div>
              <div>
                <label style={lbl}>Formula</label>
                <select className="timesheet-edit-input w-full" value={hr.entitlementFormula || 'weeks'} onChange={e => setHrField({ entitlementFormula: e.target.value })}>
                  <option value="weeks">Weeks × h/week</option>
                  <option value="days">Fixed days</option>
                  <option value="hours">Hours</option>
                </select>
              </div>
              <div>
                <label style={lbl}>Weeks</label>
                <input type="number" step="0.1" className="timesheet-edit-input w-full" value={hr.entitlementWeeks ?? ''} onChange={e => setHrField({ entitlementWeeks: e.target.value === '' ? undefined : Number(e.target.value) })} />
              </div>
              <div>
                <label style={lbl}>Base days</label>
                <input type="number" step="1" className="timesheet-edit-input w-full" value={hr.entitlementDays ?? ''} onChange={e => setHrField({ entitlementDays: e.target.value === '' ? undefined : Number(e.target.value) })} />
              </div>
              <div>
                <label style={lbl}>Leave year start</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select className="timesheet-edit-input" style={{ flex: 1 }} value={Number(mm)} onChange={e => setHrField({ leaveYearStart: `${String(e.target.value).padStart(2, '0')}-${dd}` })}>
                    {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                  <input type="number" min={1} max={31} className="timesheet-edit-input" style={{ width: 70 }} value={Number(dd)} onChange={e => setHrField({ leaveYearStart: `${mm}-${String(e.target.value).padStart(2, '0')}` })} />
                </div>
              </div>
              <div>
                <label style={lbl}>Accrual</label>
                <select className="timesheet-edit-input w-full" value={hr.accrualMode || 'upfront'} onChange={e => setHrField({ accrualMode: e.target.value })}>
                  <option value="upfront">Upfront (pro-rata)</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <div>
                <label style={lbl}>Rounding</label>
                <select className="timesheet-edit-input w-full" value={hr.rounding || 'half_up'} onChange={e => setHrField({ rounding: e.target.value })}>
                  <option value="half_up">Up to 0.5</option>
                  <option value="full_up">Up to 1</option>
                  <option value="nearest_half">Nearest 0.5</option>
                  <option value="tenth">To 0.1</option>
                  <option value="none">None</option>
                </select>
              </div>
              <div>
                <label style={lbl}>Public holidays</label>
                <select className="timesheet-edit-input w-full" value={hr.publicHolidayPolicy || 'not_deducted'} onChange={e => setHrField({ publicHolidayPolicy: e.target.value })}>
                  <option value="not_deducted">Not deducted</option>
                  <option value="deducted">Deducted from allowance</option>
                  <option value="works">Works holidays</option>
                </select>
              </div>
              <div>
                <label style={lbl}>Carryover limit (days)</label>
                <input type="number" step="0.5" className="timesheet-edit-input w-full" value={hr.carryoverMaxDays ?? ''} onChange={e => setHrField({ carryoverMaxDays: e.target.value === '' ? null : Number(e.target.value) })} />
              </div>
            </div>

            {/* Public holidays list */}
            <div className="section-label"><span className="tr-section-dot" style={{ background: '#dc2626' }} />Public holidays (seeded on company creation)</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {holidays.map((h, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input className="timesheet-edit-input" style={{ flex: 1 }} value={h.name} placeholder="Name" onChange={e => setHolidays(hs => hs.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
                  <input className="timesheet-edit-input" style={{ width: 110 }} value={h.date} placeholder="MM-DD" onChange={e => setHolidays(hs => hs.map((x, j) => j === i ? { ...x, date: e.target.value } : x))} />
                  <button type="button" className="btn-icon danger" onClick={() => setHolidays(hs => hs.filter((_, j) => j !== i))}>🗑</button>
                </div>
              ))}
              <button type="button" className="dt-btn-ghost" style={{ alignSelf: 'flex-start', fontSize: 11 }} onClick={() => setHolidays(hs => [...hs, { name: '', date: '01-01', recurring: true }])}>+ Add holiday</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CountryDefaults;
